// The page's side of the service worker (06-service-worker.md): the switch-over at the same origin
// before anything is drawn, registering the worker, handing it the session, and the update watch.
import { useSyncExternalStore } from 'react';
import { deleteAllDatabases } from './device';
import { readSession } from './session';

const SCRIPT = '/sw.js';
const MARK = 'desyr_switch_over';
const RELOADED = 'desyr_update_reloaded';

/** Every storage name of the new app (90-test-hooks.md, section 3, and 07's); per-account keys add ":<ID>". */
const OWN_KEYS = new Set(
  `desyr_session desyr_mail desyr_default_account desyr_push_off
  desyr_update_reloaded desyr_settings desyr_after_sign_in desyr_call_media desyr_companies_found desyr_last_email
  desyr_sign_in_company desyr_push_client desyr_scheduled_previews desyr_accounts
  desyr_store_prefix desyr_adding_account desyr_switched desyr_switching desyr_thread_seen desyr_mail_pending
  desyr_account_request desyr_account_started desyr_account_page desyr_password_checked desyr_token_ended desyr_company_admin desyr_setup_code
  desyr_reset_code desyr_recovery_offered desyr_switch_over desyr_last_page desyr_closed_categories
  desyr_open_rail_folders desyr_side_list_hidden desyr_plus_seen
  desyr_id desyr_id_after desyr_id_auto desyr_id_device desyr_id_hop desyr_id_leaving desyr_id_pending
  desyr_id_pending_action desyr_id_queue desyr_id_return desyr_signed_out desyr_auth_gen desyr_circle_look
  desyr_device_naming desyr_install_dismissed desyr_key_asked desyr_mic_hint_shown desyr_passkey_prompted
  desyr_phone_room desyr_picture_last desyr_test_fail desyr_unlock_asked
  desyr_databases_left desyr_id_circles desyr_id_round desyr_device_passkey_asked desyr_drafts_kept desyr_gone_rooms`.split(/\s+/),
);
const ownKey = (key: string) => OWN_KEYS.has(key.split(':')[0]);
/** The sync store, the crypto stores, the search index and the start probe. */
const ownDatabase = (name: string) => name.startsWith('desyr') || name.startsWith('matrix-js-sdk:desyr');
const ownCache = (name: string) => name.startsWith('desyr-shell-') || name === 'desyr-data';

/**
 * The switch-over (06): on the first start in a browser, before the app is drawn, every other
 * service worker, storage key, database and cache at this origin goes; the mark is written last.
 */
export async function switchOver(): Promise<void> {
  try {
    if (localStorage.getItem(MARK)) return;
  } catch {
    return; // no storage at all: nothing of another app can be kept here either
  }
  try {
    for (const registration of (await navigator.serviceWorker?.getRegistrations()) ?? []) {
      const script = (registration.active ?? registration.waiting ?? registration.installing)?.scriptURL;
      if (!script || new URL(script).pathname !== SCRIPT) await registration.unregister();
    }
    for (const store of [localStorage, sessionStorage])
      for (const key of Object.keys(store)) if (!ownKey(key)) store.removeItem(key);
    await deleteAllDatabases((name) => !ownDatabase(name));
    if (typeof caches !== 'undefined') for (const name of await caches.keys()) if (!ownCache(name)) await caches.delete(name);
    localStorage.setItem(MARK, '1');
  } catch {
    // no mark: the next start tries again
  }
}

/** The page has sent its session to the worker. */
export const SESSION_SENT = 'desyr:session-sent';
const post = (message: object) => navigator.serviceWorker?.controller?.postMessage(message);

/** Hands the worker the active session (a page not controlled by a worker sends nothing). */
export function sendSession(): void {
  const s = readSession();
  if (s) post({ type: 'desyr:session', baseUrl: s.base_url, accessToken: s.access_token });
  // The pictures that failed meanwhile try again (02, "Pictures that don't load").
  dispatchEvent(new Event(SESSION_SENT));
}

/** Signed in and open: the worker caches the rest of the app in the background (06, offline shell). */
export const cacheTheApp = () => post({ type: 'desyr:cache-app' });

/** Signing out: the worker forgets that server's session first. */
export const signedOutOf = (baseUrl: string) => post({ type: 'desyr:session', baseUrl });

let updateReady = false;
const listeners = new Set<() => void>();

/** A new version has taken over (the update notice). */
export const useUpdateReady = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => updateReady,
  );

const started = Date.now();

/** Right after a reload desyr did itself (a minute ago at most, in the new page's first 30 s): no news. */
function justReloaded(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOADED) ?? 0);
    return Date.now() - at < 60_000 && Date.now() - started < 30_000;
  } catch {
    return false;
  }
}

export function reloadForUpdate(): void {
  try {
    sessionStorage.setItem(RELOADED, String(Date.now()));
  } catch {
    // the notice may show once more
  }
  // Always reloads: a waiting version is asked to take over first, but 2 s at most (06).
  const go = () => location.reload();
  const container = navigator.serviceWorker;
  if (!container) return go();
  setTimeout(go, 2000);
  container.getRegistration().then((r) => {
    if (!r?.waiting) return go();
    container.addEventListener('controllerchange', go, { once: true });
    r.waiting.postMessage({ type: 'desyr:take-over' });
  }, go);
}

/** Registers the worker, keeps it in touch with the session, and watches for new versions. */
export function startWorker(): void {
  const container = navigator.serviceWorker;
  if (!container || import.meta.env.DEV) return;
  let hadController = !!container.controller;
  container.addEventListener('message', (e: MessageEvent) => {
    if (e.data?.type === 'desyr:hello') e.ports[0]?.postMessage('desyr');
    if (e.data?.type === 'desyr:session-request') sendSession();
  });
  container.addEventListener('controllerchange', () => {
    if (hadController && !justReloaded()) {
      updateReady = true;
      listeners.forEach((l) => l());
    }
    hadController = true;
    sendSession();
  });
  container.register(SCRIPT, { scope: '/' }).then(
    (registration) => {
      sendSession();
      void container.ready.then(sendSession);
      const check = () => registration.update().catch(() => {});
      setInterval(check, 30 * 60_000);
      addEventListener('focus', check);
    },
    () => {},
  );
}

/**
 * A part of the app loaded on first use that a new version replaced: reload into the new version,
 * at most once a minute and not while offline (06, "Watching for new versions", 5).
 */
export function reloadOnMissingParts(): void {
  addEventListener('vite:preloadError', (event) => {
    if (!navigator.onLine) return;
    try {
      if (Date.now() - Number(sessionStorage.getItem(RELOADED) ?? 0) < 60_000) return;
    } catch {
      return;
    }
    event.preventDefault();
    reloadForUpdate();
  });
}
